<template>
<div class="form-wrapper">
    <div class="confirmation">
        <card-header :title="'Start a free 14-day trial'" :designVersion="2" :subtitle="'No credit card required'" />
        <div class="body">
            <div class="spinner" :class="{'hidden': !isSubmitting}"><i class="fa fa-spinner fa-pulse"></i></div>
            <div class="form-group" :class="{'hidden': isSubmitting}">
                <input-set
                    v-model="email"
                    ref="email"
                    :type="'text'"
                    :name="'email'"
                    :label="errors.email ? errors.email : ''"
                    :placeholder="'Work email*'"
                    @inputOnEnter="signUp"
                    @inputOnBlur="checkOnInputBlur"
                    :hasError="errors.email ? true : false"
                    @inputOnType="checkOnInputType">
                </input-set>
                <password-input-with-suggestion
                    :inputLabel="''"
                    :wrapperClass="'input-set'"
                    :inputClass="'form-control'"
                    :inputName="'password'"
                    :inputPlaceholder="'Password*'"
                    :checkErrors="true"
                    :tabindex="2"
                    @onPasswordChange="onPasswordChange"
                    @onPasswordValidation="onPasswordValidation"
                    @passwordOnEnter="signUp"
                    @confirm="signUp"
                />
                <div class="button-wrapper">
                    <custom-button v-if="!isSubmitting" :text="'Sign up'" :isActive="buttonState" @enter="signUp" @click="signUp" :size="'button-large'"></custom-button>
                    <button v-else id="mock-button-to-maintain-form-height" class="btn btn-primary hidden" :isActive="false">Sign up</button>
                </div>
                <br />
                <paragraph id="agree-text" :type="'p'" :variation="'small'">
                    By clicking Sign up, I agree to Autobahn's <a tabindex="1" href="/terms" target="_blank">Terms of Use</a> and the <a tabindex="1" href="/privacy" target="_blank">Privacy Policy</a>.
                </paragraph>
            </div>
            <paragraph :class="{'hidden': isSubmitting}" id="already-registered-text" :type="'p'" :variation="'medium'">
                Already have an account? <a tabindex="1" href="/login">Log in</a>
            </paragraph>
        </div>
    </div>
</div>
</template>

<script>
/* eslint-disable import/extensions */
import '~/assets/js/global.js';
import _ from 'lodash';
import CardHeader from './card-header.vue';
import Validate from '~/mixins/validation.js';
import PasswordInputWithSuggestion from '~/components/shared/password-input-with-suggestion.vue';
import { checkIfUserExists } from '~/assets/js/utils/ab-aws-migration.js';
import Heading from '~/components/shared/heading.vue';
import Paragraph from '~/components/shared/paragraph.vue';
import InputSet from '~/components/shared/input-set.vue';
import CustomButton from '~/components/shared/custom-button.vue';

export default {
    components: {
        // eslint-disable-next-line vue/no-unused-components
        CardHeader, Heading, Paragraph, InputSet, CustomButton, PasswordInputWithSuggestion,
    },
    mixins: [Validate],
    data() {
        return {
            password: '',
            email: '',
            isSubmitting: false,
            showPassword: false,
            strength: 0,
            emptyFieldErrorMessage: 'This field cannot be empty',
        };
    },
    props: {
        userData: Object,
    },
    computed: {
        buttonState() {
            let state = this.password && this.email && this.strength >= 3;
            state = state && _.values(this.errors).every((error) => error === '');
            return !!state;
        },
        phoneNumber() {
            const finalPhoneNumber = this.phoneNumberInput ? `+${this.dialCode.toString()}${this.phoneNumberInput.toString()}` : '';
            return finalPhoneNumber;
        },
    },
    methods: {
        checkboxClass(state) {
            const icon = state ? 'check-' : '';
            const fill = state ? 'fa' : 'far';
            return `${fill} fa-${icon}square`;
        },
        signUp() {
            const self = this;
            const state = self.checkErrors();
            if (typeof posthog !== 'undefined') {
                // eslint-disable-next-line no-undef
                posthog.identify(self.email);
            }
            if (!state) {
                return;
            }
            this.isSubmitting = true;
            const { email } = self;

            this.doBackendRequest(
                'GET',
                `/api/verify_domain?email=${email}`,
                (res) => {
                    if (res.valid) {
                        checkIfUserExists(
                            email,
                            () => {},
                            (err) => {
                                if (err.code === 'NotAuthorizedException') {
                                    // user already exists
                                    self.errors.email = 'Account already exists.';
                                    self.isSubmitting = false;
                                } else if (err.code === 'UserNotFoundException') {
                                    // new user, can go to next step
                                    self.userData.email = self.email;
                                    self.userData.password = self.password;
                                    self.$emit('next_index', 1);
                                } else {
                                    self.errors.email = 'There was an error creating your account.';
                                    self.isSubmitting = false;
                                }
                            },
                        );
                    } else {
                        self.errors.email = 'Email domain is not allowed. You must use a company email.';
                        self.isSubmitting = false;
                    }
                },
                undefined,
                undefined,
            );
        },
        validIcon(state) {
            return state ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-primary';
        },
        checkEmail() {
            return this.validate('email', ['email', 'req']);
        },
        checkPassword() {
            return this.validate('password', ['password', 'req', this.strength]) && this.strength >= 3;
        },
        checkErrors() {
            let state = true;
            state = this.checkEmail() && state;
            state = this.checkPassword() && state;
            return state;
        },
        checkOnInputType(name) {
            switch (name) {
            case 'email':
                if ('email' in this.errors) {
                    this.checkEmail();
                }
                break;
            default:
                break;
            }
        },
        onPasswordChange(value) {
            this.password = value;
            this.$nextTick(() => {
                if ('password' in this.errors) {
                    this.checkPassword();
                }
            });
        },
        onPasswordValidation(value) {
            this.strength = value;
        },
        checkOnInputBlur(name) {
            switch (name) {
            case 'email':
                if (!this.errors.email) {
                    this.checkEmail();
                }
                break;
            default:
                break;
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.$refs.email.$refs.inputSet.focus();
        });
    },
};
</script>

<style lang="scss" scoped>
@import '~/scss/pages/signup.scss';
</style>
